/**
 * StatusBadge — generic, theme-aware pill for arbitrary status enums.
 *
 * Single responsibility: render a compact label with a color treatment
 * derived from one of five semantic tones (neutral, info, success, warn,
 * danger). Designed for reuse across governance UIs (lifecycle status,
 * decision verdicts, replayability grades).
 *
 * Tone palette flows through inline `style` to preserve the existing
 * test contract; layout, rounding, typography, and tracking are
 * Tailwind utilities so consumers can extend via `className`.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, PRIMARY, SAFE, WARN } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export type StatusTone = "neutral" | "info" | "success" | "warn" | "danger";

export interface StatusBadgeProps {
  readonly label: string;
  readonly tone: StatusTone;
  readonly title?: string;
  /** Additional Tailwind class names appended after the defaults. */
  readonly className?: string;
}

interface TonePalette {
  readonly bg: string;
  readonly border: string;
  readonly fg: string;
}

const PALETTE: Readonly<Record<StatusTone, TonePalette>> = {
  neutral: { bg: GRAY[100], border: GRAY[300], fg: GRAY[700] },
  info: { bg: PRIMARY[100], border: PRIMARY[300], fg: PRIMARY[700] },
  success: { bg: SAFE[100], border: SAFE[300], fg: SAFE[700] },
  warn: { bg: WARN[100], border: WARN[300], fg: WARN[700] },
  danger: { bg: WARN[300], border: WARN[500], fg: WARN[700] },
} as const;

/**
 * Renders a status pill. `tone` is mapped to a palette derived from CSK
 * design tokens; `label` is rendered verbatim (theming, not formatting).
 */
export const StatusBadge = React.memo(function StatusBadge({
  label,
  tone,
  title,
  className,
}: StatusBadgeProps): React.JSX.Element {
  const palette = PALETTE[tone];
  return (
    <span
      title={title}
      className={cn(
        "inline-block rounded-full px-2 py-0.5 text-xs font-semibold tracking-wide font-body",
        className,
      )}
      style={{
        backgroundColor: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
      }}
    >
      {label}
    </span>
  );
});
